import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../Common/Modal';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import Alert from '../Common/Alert';
import { ElectronicSign } from '../Common/ElectronicSign';
import visitService from '../../services/visitService';
import userService from '../../services/userService';
import { VISIT_TYPES } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import VisitModalHeader from './VisitModalHeader';
import './ConfirmPIModal.css';

const toLocalYmd = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const ConfirmPIModal = ({ isOpen, onClose, visit, resolvedPiName, onConfirmed }) => {
  const [confirmationMode, setConfirmationMode] = useState('');
  const [confirmedOn, setConfirmedOn] = useState('');
  const [confirmationSentTo, setConfirmationSentTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pmUsers, setPmUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [visitDetail, setVisitDetail] = useState(null);
  const [confirmReject, setConfirmReject] = useState({ open: false });
  const [toast, setToast] = useState(null);
  const [showESignModal, setShowESignModal] = useState(false);
  const [showRejectESignModal, setShowRejectESignModal] = useState(false);
  const eSignRemarksRef = useRef('');

  useEffect(() => {
    if (isOpen && visit) {
      const defaultDate = toLocalYmd();
      setConfirmationMode('');
      setConfirmedOn(defaultDate);
      setConfirmationSentTo('');
      setFieldErrors({});
    } else if (!isOpen) {
      setConfirmationMode('');
      setConfirmedOn('');
      setConfirmationSentTo('');
      setSaving(false);
      setPmUsers([]);
      setLoadingUsers(false);
      setVisitDetail(null);
      setFieldErrors({});
      setToast(null);
      setShowESignModal(false);
      setShowRejectESignModal(false);
      setConfirmReject({ open: false });
    }
  }, [isOpen, visit]);

  // Fetch visit by id when modal opens to get visitLetterDate for Confirmation Date validation
  useEffect(() => {
    if (!isOpen || !visit) {
      setVisitDetail(null);
      return;
    }
    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    if (!visitId) {
      setVisitDetail(null);
      return;
    }
    let cancelled = false;
    visitService.getById(visitId).then((detail) => {
      if (!cancelled) setVisitDetail(detail);
    }).catch((err) => {
      if (!cancelled) {
        console.error('Error loading visit for Confirm PI', err);
        setVisitDetail(null);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, visit]);

  // Load PM users when the modal is opened
  useEffect(() => {
    const loadPmUsers = async () => {
      if (!isOpen) return;
      try {
        setLoadingUsers(true);
        const users = await userService.getByRole('PM');
        setPmUsers(users || []);
      } catch (err) {
        console.error('Error loading PM users:', err);
        setPmUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadPmUsers();
  }, [isOpen]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Confirm PI Availability">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const handleClear = () => {
    setFieldErrors({});
    const defaultDate = toLocalYmd();
    setConfirmationMode('');
    setConfirmedOn(defaultDate);
    setConfirmationSentTo('');
  };

  const validateForm = () => {
    const errs = {};
    const visitForValidation = visitDetail ?? visit;
    if (!(confirmationMode || '').trim()) errs.confirmationMode = visitMessages.confirmationModeRequired;
    if (!(confirmedOn || '').trim()) errs.confirmedOn = visitMessages.confirmedOnRequired;
    else {
      const todayYmd = toLocalYmd();
      if (confirmedOn < todayYmd) {
        errs.confirmedOn = visitMessages.confirmedOnNotPast;
      } else {
        const chosen = new Date(confirmedOn);
        chosen.setHours(0, 0, 0, 0);
        if (visitForValidation?.plannedStartDate) {
          const scheduledStart = new Date(visitForValidation.plannedStartDate);
          scheduledStart.setHours(0, 0, 0, 0);
          if (chosen < scheduledStart) errs.confirmedOn = visitMessages.confirmedOnBeforeScheduledVisit;
        }
        if (!errs.confirmedOn && visitForValidation?.visitLetterDate) {
          const letterDate = new Date(visitForValidation.visitLetterDate);
          letterDate.setHours(0, 0, 0, 0);
          if (chosen < letterDate) errs.confirmedOn = visitMessages.confirmedOnBeforeVisitLetter;
        }
      }
    }
    if (!(confirmationSentTo || '').trim()) errs.confirmationSentTo = visitMessages.confirmationRecipientRequired;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setFieldErrors({});
    if (!validateForm()) return;
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = async () => {
    setShowESignModal(false);
    setSaving(true);
    try {
      const confirmedOnVal = (confirmedOn || '').trim() ? confirmedOn : null;
      const confirmationSentToVal = (confirmationSentTo || '').trim() ? Number(confirmationSentTo) : null;
      const confirmationModeVal = (confirmationMode || '').trim() || null;
      const remarkVal = (eSignRemarksRef.current != null && typeof eSignRemarksRef.current === 'string') ? eSignRemarksRef.current.trim() : '';
      await visitService.confirm(visit.scheduledVisitID, {
        confirmationMode: confirmationModeVal,
        confirmedOn: confirmedOnVal,
        confirmationSentToUserID: confirmationSentToVal,
        remark: remarkVal || undefined,
      });
      if (onConfirmed) onConfirmed();
      setToast({ type: 'success', message: commonMessages.authSuccess });
      onClose();
    } catch (err) {
      console.error('Error confirming PI availability:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to confirm PI availability. Please try again.';
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleRejectClick = () => {
    if (!visit.scheduledVisitID) return;
    setConfirmReject({
      open: true,
      title: 'Reject visit',
      message: 'Are you sure you want to reject? The visit will be marked inactive.',
      onConfirm: () => {
        setConfirmReject({ open: false });
        setShowRejectESignModal(true);
      },
    });
  };

  const runAfterRejectESign = () => {
    setShowRejectESignModal(false);
    handleReject();
  };

  const handleReject = async () => {
    if (!visit.scheduledVisitID) return;
    try {
      setSaving(true);
      await visitService.cancel(visit.scheduledVisitID, 'Rejected at Confirm PI');
      if (onConfirmed) onConfirmed();
      setToast({ type: 'success', message: commonMessages.authSuccess });
      onClose();
    } catch (err) {
      console.error('Error rejecting visit:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to mark visit inactive.';
      const isAlreadyRejected = msg === 'Already Rejected';
      setToast({ type: isAlreadyRejected ? 'info' : 'error', message: isAlreadyRejected ? 'Already Rejected' : msg });
    } finally {
      setSaving(false);
    }
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';
  const confirmedOnMinYmd = toLocalYmd();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title="Confirm PI Availability"
      className="confirm-pi-modal"
    >
      <form onSubmit={handleSubmit}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input
            type="text"
            className="form-input"
            value={visitTypeLabel}
            disabled
          />
        </div>

        <div className="form-group">
          <label className="form-label">Confirmation Mode <span className="required-asterisk">*</span></label>
          <select
            className="form-select"
            value={confirmationMode}
            onChange={(e) => { setConfirmationMode(e.target.value); setFieldErrors(prev => ({ ...prev, confirmationMode: '' })); }}
          >
            <option value="">Select Confirmation Mode</option>
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
            <option value="Teams">Teams</option>
            <option value="Other">Other</option>
          </select>
          {fieldErrors.confirmationMode && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmationMode}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Confirmed On <span className="required-asterisk">*</span></label>
          <input
            type="date"
            className="form-input"
            value={confirmedOn}
            min={confirmedOnMinYmd}
            onChange={(e) => { setConfirmedOn(e.target.value); setFieldErrors(prev => ({ ...prev, confirmedOn: '' })); }}
          />
          {fieldErrors.confirmedOn && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmedOn}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Confirmation Sent To <span className="required-asterisk">*</span></label>
          <select
            className="form-select"
            value={confirmationSentTo}
            onChange={(e) => { setConfirmationSentTo(e.target.value); setFieldErrors(prev => ({ ...prev, confirmationSentTo: '' })); }}
            disabled={loadingUsers || pmUsers.length === 0}
          >
            <option value="">
              {loadingUsers
                ? 'Loading PM users...'
                : pmUsers.length === 0
                  ? 'No PM users available'
                  : 'Select Confirmation Sent To'}
            </option>
            {pmUsers.map((user) => (
              <option key={user.userId} value={user.userId}>
                {user.fullName ?? user.FullName ?? ''}
              </option>
            ))}
          </select>
          {fieldErrors.confirmationSentTo && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.confirmationSentTo}</div>}
        </div>

        <div className="confirm-pi-footer">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClear}
            disabled={saving}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </form>
      {confirmReject.open && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmReject({ open: false })}
          onConfirm={confirmReject.onConfirm}
          title={confirmReject.title}
          message={confirmReject.message}
          confirmText="Yes"
          cancelText="No"
          variant="danger"
        />
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
      {showESignModal && (
        <ElectronicSign
          show={true}
          onClose={() => setShowESignModal(false)}
          onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
          onSave={runAfterESign}
        />
      )}
      {showRejectESignModal && (
        <ElectronicSign
          show={true}
          onClose={() => setShowRejectESignModal(false)}
          onAuthSuccess={() => {}}
          onSave={runAfterRejectESign}
        />
      )}
    </Modal>
  );
};

export default ConfirmPIModal;

